import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatUTCDateTimeToLocal } from '../../../utils/timezone';
import { getAdminHeaders } from '../../../utils';
import './ReportsTimePage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface TimeReportEntry {
  id: number;
  volunteerId: number;
  firstName: string;
  lastName: string;
  taskId: number;
  taskName: string;
  taskCategory: string;
  taskColor?: string;
  checkInTime: string;
  checkOutTime: string;
  duration: number;
  date: string;
  workLocation: string;
  wasAutoCheckedOut?: boolean;
}

interface TimeReportSummary {
  byTask: { [key: string]: number };
  byCategory: { [key: string]: number };
  byVolunteer: { [key: string]: number };
  byDate: { [key: string]: number };
  byWorkLocation: { [key: string]: number };
}

interface TimeReportsResponse {
  totalHours: number;
  totalSessions: number;
  averageSession: number;
  reports: TimeReportEntry[];
  summary: TimeReportSummary;
}

interface SummaryStats {
  totalSessions: number;
  totalHours: number;
  uniqueVolunteers: number;
  uniqueTasks: number;
}

interface TaskOption {
  id: number;
  name: string;
  category: string;
}

const pacificYmd = (date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);

const defaultFilters = () => {
  const endDate = pacificYmd();
  const [year, month, day] = endDate.split('-').map(Number);
  const start = new Date(Date.UTC(year - 1, month - 1, day));
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate,
    category: '',
    taskId: '',
    workLocation: '',
  };
};

const formatHours = (hours: number): string =>
  hours.toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 0 });

const formatNiceDate = (ymd: string): string => {
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return ymd;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const isOnsiteLocation = (location: string): boolean => {
  const normalized = (location || '').toLowerCase().replace(/_/g, '-').trim();
  return normalized === 'on-site' || normalized === 'onsite';
};

const weekdayName = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    weekday: 'long',
    timeZone: 'America/Los_Angeles',
  });

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const ReportsTimePage: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<TimeReportsResponse | null>(null);
  const [summary, setSummary] = useState<SummaryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);
  const [tasks, setTasks] = useState<TaskOption[]>([]);
  const [currentView, setCurrentView] = useState<'table' | 'reports'>('table');

  useEffect(() => {
    fetchSummary();
    fetchReports(defaultFilters());
    fetchTasks();
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/time-reports/summary`, {
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      } else if (response.status === 401) {
        navigate('/admin-login');
      }
    } catch (error) {
      // Failed to fetch summary - continue without it
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tasks`);
      if (!response.ok) return;
      const data = await response.json();
      const list: TaskOption[] = ((data.tasks || []) as TaskOption[])
        .filter((task) => task.id && task.name)
        .map((task) => ({
          id: task.id,
          name: task.name,
          category: (task.category || '').trim(),
        }));
      setTasks(list);
    } catch {
      // Keep empty dropdowns if tasks cannot be loaded
    }
  };

  const fetchReports = async (activeFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilters.startDate) params.append('startDate', activeFilters.startDate);
      if (activeFilters.endDate) params.append('endDate', activeFilters.endDate);
      if (activeFilters.category) params.append('category', activeFilters.category);
      if (activeFilters.taskId) params.append('taskId', activeFilters.taskId);
      if (activeFilters.workLocation) params.append('workLocation', activeFilters.workLocation);

      const response = await fetch(`${API_BASE_URL}/time-reports?${params}`, {
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data);
      } else if (response.status === 401) {
        navigate('/admin-login');
      }
    } catch (error) {
      // Failed to fetch reports - continue without them
    } finally {
      setLoading(false);
    }
  };

  const categories = Array.from(
    new Set(tasks.map((task) => task.category).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const taskOptions = filters.category
    ? tasks.filter((task) => task.category === filters.category)
    : tasks;

  const taskLabel = (name: string) =>
    name.includes(' - ') ? name.split(' - ').slice(1).join(' - ') : name;

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'category' && prev.taskId) {
        const selected = tasks.find((task) => String(task.id) === prev.taskId);
        if (selected && value && selected.category !== value) {
          next.taskId = '';
        }
      }
      if (key === 'taskId' && value) {
        const selected = tasks.find((task) => String(task.id) === value);
        if (selected?.category) {
          next.category = selected.category;
        }
      }
      return next;
    });
  };

  const handleApplyFilters = () => {
    fetchReports(filters);
  };

  const handleClearFilters = () => {
    const reset = defaultFilters();
    setFilters(reset);
    fetchReports(reset);
  };

  const exportToCSV = () => {
    if (!reports) return;

    const csvContent = [
      ['Volunteer', 'Category', 'Task', 'Work Location', 'Check In', 'Check Out', 'Duration (Hours)'],
      ...reports.reports.map(entry => {
        const { category, task } = splitTaskName(entry.taskName, entry.taskCategory);
        const duration = entry.duration.toFixed(2);
        return [
          `${entry.firstName} ${entry.lastName}`,
          category,
          task,
          entry.workLocation,
          formatUTCDateTimeToLocal(entry.checkInTime),
          formatUTCDateTimeToLocal(entry.checkOutTime),
          duration,
        ];
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-reports-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDuration = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Split task name into category and task parts
  const splitTaskName = (taskName: string, taskCategory: string) => {
    // If task name contains " - ", split it
    if (taskName.includes(' - ')) {
      const parts = taskName.split(' - ');
      return {
        category: parts[0],
        task: parts.slice(1).join(' - ') // In case there are multiple " - " separators
      };
    }
    // If no dash, category and task are the same
    return {
      category: taskCategory || taskName,
      task: taskName
    };
  };

  const handleBack = () => {
    navigate('/admin-panel');
  };

  const overview = useMemo(() => {
    const entries = reports?.reports || [];
    const volunteerMap = new Map<string, { hours: number; sessions: number }>();
    const categoryMap = new Map<string, { hours: number; sessions: number }>();
    const weekdayMap = new Map<string, number>();
    let onsiteHours = 0;
    let remoteHours = 0;
    let onsiteSessions = 0;
    let remoteSessions = 0;
    let autoCheckouts = 0;

    for (const entry of entries) {
      const hours = entry.duration || 0;
      const name = `${entry.firstName} ${entry.lastName}`.trim() || 'Unknown';
      const { category } = splitTaskName(entry.taskName, entry.taskCategory);
      const volunteer = volunteerMap.get(name) || { hours: 0, sessions: 0 };
      volunteer.hours += hours;
      volunteer.sessions += 1;
      volunteerMap.set(name, volunteer);

      const cat = categoryMap.get(category) || { hours: 0, sessions: 0 };
      cat.hours += hours;
      cat.sessions += 1;
      categoryMap.set(category, cat);

      if (isOnsiteLocation(entry.workLocation)) {
        onsiteHours += hours;
        onsiteSessions += 1;
      } else {
        remoteHours += hours;
        remoteSessions += 1;
      }

      if (entry.wasAutoCheckedOut) autoCheckouts += 1;

      const day = weekdayName(entry.checkInTime);
      weekdayMap.set(day, (weekdayMap.get(day) || 0) + hours);
    }

    const totalHours = entries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
    const totalSessions = entries.length;
    const uniqueVolunteers = volunteerMap.size;
    const avgHours = totalSessions > 0 ? totalHours / totalSessions : 0;
    const share = (hours: number) => (totalHours > 0 ? Math.round((hours / totalHours) * 100) : 0);

    const categoryRows = Array.from(categoryMap.entries())
      .map(([name, stats]) => ({ name, ...stats, share: share(stats.hours) }))
      .sort((a, b) => b.hours - a.hours);

    const volunteers = Array.from(volunteerMap.entries())
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => b.hours - a.hours);

    let busiestWeekday = '';
    let busiestWeekdayHours = 0;
    weekdayMap.forEach((hours, day) => {
      if (hours > busiestWeekdayHours) {
        busiestWeekday = day;
        busiestWeekdayHours = hours;
      }
    });

    return {
      totalHours,
      totalSessions,
      uniqueVolunteers,
      avgHours,
      onsiteHours,
      remoteHours,
      onsiteSessions,
      remoteSessions,
      onsiteShare: share(onsiteHours),
      remoteShare: share(remoteHours),
      autoCheckouts,
      categories: categoryRows,
      volunteers,
      busiestWeekday,
      busiestCategory: categoryRows[0]?.name || '',
    };
  }, [reports]);

  const filterScope = useMemo(() => {
    const parts: string[] = [];
    if (filters.startDate && filters.endDate) {
      parts.push(`${formatNiceDate(filters.startDate)} – ${formatNiceDate(filters.endDate)}`);
    } else if (filters.startDate) {
      parts.push(`From ${formatNiceDate(filters.startDate)}`);
    } else if (filters.endDate) {
      parts.push(`Through ${formatNiceDate(filters.endDate)}`);
    }
    if (filters.category) parts.push(filters.category);
    if (filters.taskId) {
      const selected = tasks.find((task) => String(task.id) === filters.taskId);
      if (selected) parts.push(taskLabel(selected.name));
    }
    if (filters.workLocation) {
      parts.push(isOnsiteLocation(filters.workLocation) ? 'On-site' : 'Remote');
    }
    return parts.join(' · ');
  }, [filters, tasks]);

  const exportToPDF = () => {
    const generated = new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const scope = filterScope ? escapeHtml(filterScope) : '';

    const categoryRows = overview.categories
      .map(
        (row) =>
          `<tr><td>${escapeHtml(row.name)}</td><td>${escapeHtml(formatHours(row.hours))}</td><td>${row.sessions.toLocaleString()}</td><td>${row.share}%</td></tr>`
      )
      .join('');

    const volunteerRows = overview.volunteers
      .map(
        (row) =>
          `<tr><td>${escapeHtml(row.name)}</td><td>${escapeHtml(formatHours(row.hours))}</td><td>${row.sessions.toLocaleString()}</td></tr>`
      )
      .join('');

    const body =
      overview.totalSessions === 0
        ? `<p>No sessions match these filters.</p>`
        : `
        <p class="blurb">
          ${overview.uniqueVolunteers} ${overview.uniqueVolunteers === 1 ? 'person' : 'people'} volunteered
          ${escapeHtml(formatHours(overview.totalHours))} hours across ${overview.totalSessions.toLocaleString()}
          ${overview.totalSessions === 1 ? 'visit' : 'visits'}.
          ${
            overview.onsiteShare >= overview.remoteShare
              ? `Most of that time was on-site (${overview.onsiteShare}%).`
              : `Most of that time was remote (${overview.remoteShare}%).`
          }
          ${overview.busiestCategory ? ` ${escapeHtml(overview.busiestCategory)} had the most hours.` : ''}
        </p>
        <table class="stats">
          <tr>
            <td><strong>${escapeHtml(formatHours(overview.totalHours))}</strong><br>Total hours</td>
            <td><strong>${overview.totalSessions.toLocaleString()}</strong><br>Visits</td>
            <td><strong>${overview.uniqueVolunteers.toLocaleString()}</strong><br>People</td>
            <td><strong>${escapeHtml(formatDuration(overview.avgHours))}</strong><br>Typical visit</td>
          </tr>
        </table>
        <table class="stats">
          <tr>
            <td>
              <div class="muted">On-site</div>
              <strong>${escapeHtml(formatHours(overview.onsiteHours))} hours</strong><br>
              ${overview.onsiteShare}% · ${overview.onsiteSessions.toLocaleString()} visits
            </td>
            <td>
              <div class="muted">Remote</div>
              <strong>${escapeHtml(formatHours(overview.remoteHours))} hours</strong><br>
              ${overview.remoteShare}% · ${overview.remoteSessions.toLocaleString()} visits
            </td>
          </tr>
        </table>
        <h2>Hours by category</h2>
        <table>
          <thead><tr><th>Category</th><th>Hours</th><th>Visits</th><th>Share</th></tr></thead>
          <tbody>${categoryRows}</tbody>
        </table>
        <h2>People with the most hours</h2>
        <table>
          <thead><tr><th>Volunteer</th><th>Hours</th><th>Visits</th></tr></thead>
          <tbody>${volunteerRows}</tbody>
        </table>
        <p class="notes">
          ${overview.busiestWeekday ? `${escapeHtml(overview.busiestWeekday)}s had the most hours. ` : ''}
          A typical visit lasted ${escapeHtml(formatDuration(overview.avgHours))}.
          ${
            overview.autoCheckouts > 0
              ? ` ${overview.autoCheckouts.toLocaleString()} ${overview.autoCheckouts === 1 ? 'visit was' : 'visits were'} auto-checked out.`
              : ''
          }
        </p>
      `;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>FabMo Time Reports</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 24px; color: #1f2937; }
          h1 { text-align: center; font-size: 22px; margin: 0 0 8px; }
          h2 { font-size: 16px; margin: 24px 0 8px; }
          .scope, .generated { text-align: center; color: #6b7280; margin: 0 0 6px; font-size: 13px; }
          .blurb { font-size: 15px; line-height: 1.5; margin: 16px 0 20px; }
          .muted { color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 4px; }
          .notes { color: #6b7280; font-size: 13px; margin-top: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; font-size: 13px; }
          th { background: #f3f4f6; }
          table.stats td { text-align: center; border: 1px solid #d1d5db; padding: 12px; }
          table.stats { margin-bottom: 12px; }
          @media print { body { margin: 12px; } }
        </style>
      </head>
      <body>
        <h1>Time Reports</h1>
        ${scope ? `<p class="scope">${scope}</p>` : ''}
        <p class="generated">Generated ${escapeHtml(generated)}</p>
        ${body}
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (loading && !reports) {
    return (
      <div className="reports-time-container">
        <div className="reports-time-card">
          <div className="loading-spinner"></div>
          <p>Loading time reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-time-container">
      <div className="reports-time-card">
        <div className="reports-time-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="reports-time-title">Time Reports</h1>
          <p className="reports-time-subtitle">
            See who volunteered, for how long, and where the hours went
          </p>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <h3>Filters</h3>
          <div className="filters-grid">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Category</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Task</label>
              <select
                value={filters.taskId}
                onChange={(e) => handleFilterChange('taskId', e.target.value)}
              >
                <option value="">All Tasks</option>
                {taskOptions.map((task) => (
                  <option key={task.id} value={task.id}>{taskLabel(task.name)}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Work Location</label>
              <select
                value={filters.workLocation}
                onChange={(e) => handleFilterChange('workLocation', e.target.value)}
              >
                <option value="">All Locations</option>
                <option value="remote">Remote</option>
                <option value="on-site">On-Site</option>
              </select>
            </div>
          </div>
          <div className="filter-actions">
            <button onClick={handleApplyFilters} className="apply-filters-btn">
              Apply Filters
            </button>
            <button onClick={handleClearFilters} className="clear-filters-btn">
              Clear Filters
            </button>
          </div>
        </div>

        <div className="view-toggle">
          <div className="view-toggle-tabs">
            <button
              className={currentView === 'table' ? 'active' : ''}
              onClick={() => setCurrentView('table')}
            >
              Table View
            </button>
            <button
              className={currentView === 'reports' ? 'active' : ''}
              onClick={() => setCurrentView('reports')}
            >
              Reports
            </button>
          </div>
          <button
            type="button"
            className="export-btn"
            onClick={currentView === 'reports' ? exportToPDF : exportToCSV}
          >
            {currentView === 'reports' ? 'Export to PDF' : 'Export to CSV'}
          </button>
        </div>

        {/* Reports Content */}
        {currentView === 'table' && reports && (
          <div className="reports-table-section">
            <div className="summary-stats">
              <div className="stat-card">
                <h3>{reports.totalSessions}</h3>
                <p>Total Sessions</p>
              </div>
              <div className="stat-card">
                <h3>{reports.totalHours.toFixed(1)}h</h3>
                <p>Total Hours</p>
              </div>
              <div className="stat-card">
                <h3>{Object.keys(reports.summary.byVolunteer).length}</h3>
                <p>Active Volunteers</p>
              </div>
              <div className="stat-card">
                <h3>{Object.keys(reports.summary.byTask).length}</h3>
                <p>Tasks Performed</p>
              </div>
            </div>
            <h3>Time Reports ({reports.totalSessions} sessions, {reports.totalHours.toFixed(1)} hours)</h3>
            <p className="auto-checkout-note">
              Any duration with an underline was an auto checkout.
            </p>
            <div className="table-container">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Volunteer</th>
                    <th>Category</th>
                    <th>Task</th>
                    <th>Work Location</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.reports.map((entry) => {
                    const { category, task } = splitTaskName(entry.taskName, entry.taskCategory);
                    const categoryColor = entry.taskColor || '#f3f4f6';
                    // Determine text color based on background brightness
                    const getTextColor = (bgColor: string) => {
                      // Convert hex to RGB
                      let hex = bgColor.replace('#', '');
                      // Handle 3-character hex codes
                      if (hex.length === 3) {
                        hex = hex.split('').map(char => char + char).join('');
                      }
                      if (hex.length !== 6) {
                        return '#1f2937'; // Default to dark text
                      }
                      const r = parseInt(hex.substring(0, 2), 16);
                      const g = parseInt(hex.substring(2, 4), 16);
                      const b = parseInt(hex.substring(4, 6), 16);
                      // Calculate luminance
                      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                      // Use dark text for light backgrounds, light text for dark backgrounds
                      return luminance > 0.5 ? '#1f2937' : '#ffffff';
                    };
                    const textColor = getTextColor(categoryColor);
                    // Create a slightly darker border color
                    const darkenColor = (color: string, percent: number) => {
                      let hex = color.replace('#', '');
                      if (hex.length === 3) {
                        hex = hex.split('').map(char => char + char).join('');
                      }
                      if (hex.length !== 6) return color;
                      const r = Math.max(0, parseInt(hex.substring(0, 2), 16) - Math.round(255 * percent));
                      const g = Math.max(0, parseInt(hex.substring(2, 4), 16) - Math.round(255 * percent));
                      const b = Math.max(0, parseInt(hex.substring(4, 6), 16) - Math.round(255 * percent));
                      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                    };
                    const borderColor = darkenColor(categoryColor, 0.15);
                    return (
                      <tr key={entry.id}>
                        <td>{entry.firstName} {entry.lastName}</td>
                        <td>
                          <span 
                            className="category-badge"
                            style={{
                              backgroundColor: categoryColor,
                              color: textColor,
                              border: `1px solid ${borderColor}`
                            }}
                          >
                            {category}
                          </span>
                        </td>
                        <td>{task}</td>
                        <td>{entry.workLocation}</td>
                        <td>{formatUTCDateTimeToLocal(entry.checkInTime)}</td>
                        <td>{formatUTCDateTimeToLocal(entry.checkOutTime)}</td>
                        <td>
                          <span className={entry.wasAutoCheckedOut ? 'duration-auto-checkout' : undefined}>
                            {formatDuration(entry.duration)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {currentView === 'reports' && reports && (
          <div className="overview-section">
            <h3 className="overview-title">Time Reports</h3>
            {filterScope && <p className="overview-scope">{filterScope}</p>}

            {overview.totalSessions === 0 ? (
              <p className="overview-empty">No sessions match these filters.</p>
            ) : (
              <>
                <p className="overview-blurb">
                  {overview.uniqueVolunteers} {overview.uniqueVolunteers === 1 ? 'person' : 'people'} volunteered{' '}
                  {formatHours(overview.totalHours)} hours across {overview.totalSessions.toLocaleString()}{' '}
                  {overview.totalSessions === 1 ? 'visit' : 'visits'}.
                  {overview.onsiteShare >= overview.remoteShare
                    ? ` Most of that time was on-site (${overview.onsiteShare}%).`
                    : ` Most of that time was remote (${overview.remoteShare}%).`}
                  {overview.busiestCategory ? ` ${overview.busiestCategory} had the most hours.` : ''}
                </p>

                <div className="summary-stats">
                  <div className="stat-card">
                    <h3>{formatHours(overview.totalHours)}</h3>
                    <p>Total hours</p>
                  </div>
                  <div className="stat-card">
                    <h3>{overview.totalSessions.toLocaleString()}</h3>
                    <p>Visits</p>
                  </div>
                  <div className="stat-card">
                    <h3>{overview.uniqueVolunteers.toLocaleString()}</h3>
                    <p>People</p>
                  </div>
                  <div className="stat-card">
                    <h3>{formatDuration(overview.avgHours)}</h3>
                    <p>Typical visit</p>
                  </div>
                </div>

                <div className="overview-location-row">
                  <div className="overview-location-card">
                    <p className="overview-location-label">On-site</p>
                    <p className="overview-location-value">{formatHours(overview.onsiteHours)} hours</p>
                    <p className="overview-location-meta">
                      {overview.onsiteShare}% · {overview.onsiteSessions.toLocaleString()} visits
                    </p>
                  </div>
                  <div className="overview-location-card">
                    <p className="overview-location-label">Remote</p>
                    <p className="overview-location-value">{formatHours(overview.remoteHours)} hours</p>
                    <p className="overview-location-meta">
                      {overview.remoteShare}% · {overview.remoteSessions.toLocaleString()} visits
                    </p>
                  </div>
                </div>

                <div className="overview-block">
                  <h4>Hours by category</h4>
                  <div className="overview-table-wrap">
                    <table className="overview-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Hours</th>
                          <th>Visits</th>
                          <th>Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.categories.map((row) => (
                          <tr key={row.name}>
                            <td>{row.name}</td>
                            <td>{formatHours(row.hours)}</td>
                            <td>{row.sessions.toLocaleString()}</td>
                            <td>{row.share}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="overview-block">
                  <h4>People with the most hours</h4>
                  <div className="overview-table-wrap overview-table-scroll">
                    <table className="overview-table">
                      <thead>
                        <tr>
                          <th>Volunteer</th>
                          <th>Hours</th>
                          <th>Visits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.volunteers.slice(0, 15).map((row) => (
                          <tr key={row.name}>
                            <td>{row.name}</td>
                            <td>{formatHours(row.hours)}</td>
                            <td>{row.sessions.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {overview.volunteers.length > 15 && (
                    <p className="overview-footnote">
                      Showing the 15 people with the most hours of {overview.volunteers.length} volunteers.
                    </p>
                  )}
                </div>

                <p className="overview-notes">
                  {overview.busiestWeekday ? `${overview.busiestWeekday}s had the most hours. ` : ''}
                  A typical visit lasted {formatDuration(overview.avgHours)}.
                  {overview.autoCheckouts > 0
                    ? ` ${overview.autoCheckouts.toLocaleString()} ${overview.autoCheckouts === 1 ? 'visit was' : 'visits were'} auto-checked out.`
                    : ''}
                </p>
              </>
            )}
          </div>
        )}

        <div className="reports-time-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Admin Panel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportsTimePage;
